// Formats a user for display. Default is the "short" form ("Scott M.") used in
// tight cells (rollout master grid, filter bars, bulk action pickers, contact
// primary tag). Pass { full: true } for the full name (sign-in header, profile,
// audit log).
//
// Rules:
//   - Split displayName on the LAST space. First token is the "first name"; last
//     token's initial becomes the trailing initial. Middle tokens (e.g. "van
//     der" in "Scott van der Berg") are preserved verbatim so we render
//     "Scott van der B." -- slightly weird but accurate.
//   - Single-word displayName returns the whole word.
//   - Empty/null displayName falls back to the email local-part (before '@').
//   - Missing email + missing displayName falls back to "(unknown user)".
//
// User type carries displayName as a single string (from Google Sign-in), so
// there's no persisted firstName/lastName to consult -- everything is derived.

type UserLike = {
  displayName?: string | null;
  email?: string | null;
};

export type FormatUserOptions = {
  full?: boolean;
};

export function formatUserDisplayName(
  user: UserLike | null | undefined,
  options: FormatUserOptions = {},
): string {
  if (!user) return '(unknown user)';
  const name = (user.displayName ?? '').trim();
  if (options.full) {
    if (name) return name;
    return emailLocalPart(user.email) ?? '(unknown user)';
  }
  if (!name) return emailLocalPart(user.email) ?? '(unknown user)';

  // Split on the LAST space so multi-part surnames render as
  // "Scott van der B." rather than "Scott v."
  const lastSpace = name.lastIndexOf(' ');
  if (lastSpace < 0) return name;
  const head = name.slice(0, lastSpace).trim();
  const tail = name.slice(lastSpace + 1).trim();
  if (!tail) return head;
  return `${head} ${tail.charAt(0).toUpperCase()}.`;
}

function emailLocalPart(email: string | null | undefined): string | null {
  if (!email) return null;
  const at = email.indexOf('@');
  return at > 0 ? email.slice(0, at) : email;
}
